import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { useTextoCadaMeses } from '#components/mobile/anual/PagosFijosMensuales';
import { TODAS_LAS_CUENTAS } from '#components/mobile/budget/cuentaCategoria';
import { useGuardarObjetivo } from '#components/mobile/budget/ObjetivoPage';
import type { Objetivo } from '#components/mobile/budget/objetivos';
import { lineasDeObjetivo } from '#components/mobile/budget/objetivos';
import {
  cobroReal,
  detectarCambioCuenta,
  frecuenciaDeObjetivo,
  inferirCuenta,
  inferirDiaCargo,
  MESES_COBRO,
} from '#components/mobile/budget/pagosFijos';
import type { CobroReal } from '#components/mobile/budget/pagosFijos';
import {
  useCuentasPagosFijos,
  useFrecuenciasPagosFijos,
  useHistorialCategorias,
  useIgnoradosPagosFijos,
} from '#components/mobile/budget/usePagosFijos';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, TACTIL } from '#components/mobile/ui/tokens';
import { useAccounts } from '#hooks/useAccounts';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';

import { HojaCuentaCategoria } from './CuentaFicha';

/** Texto de la frecuencia del objetivo («al mes», «cada 12 meses»…). */
export function useTextoFrecuencia() {
  const { t } = useTranslation();
  return (objetivo: Objetivo): string => {
    const f = frecuenciaDeObjetivo(objetivo);
    if (!f) {
      return '';
    }
    if (f.tipo === 'mensual') {
      return t('every month');
    }
    if (f.tipo === 'una-vez') {
      return t('once');
    }
    return f.meses === 12
      ? t('every year')
      : t('every {{count}} months', { count: f.meses });
  };
}

/**
 * «Se cobra cada 2 meses · unos 45 € · hacia el día 25 (según tu
 * historial)»: cómo se cobra de verdad, aparte de lo que se aparta. Con el
 * día fijado a mano, «el día 25». Es la ÚNICA línea de frecuencia, importe y
 * día de la ficha (el día se cambia desde su hoja).
 */
export function useTextoCobro() {
  const { t } = useTranslation();
  const format = useFormat();
  const textoCada = useTextoCadaMeses();
  return (cobro: CobroReal, dia: number | null, diaFijo = false): string => {
    const partes = [
      cobro.meses === 'irregular'
        ? t('Charged irregularly')
        : t('Charged {{frequency}}', {
            frequency: textoCada(cobro.meses ?? 1),
          }),
    ];
    if (cobro.importeTipico != null) {
      partes.push(
        t('about {{amount}}', {
          amount: format(cobro.importeTipico, 'financial'),
        }),
      );
    }
    // Irregular: sin día previsto.
    if (dia != null && cobro.meses !== 'irregular') {
      partes.push(
        diaFijo
          ? t('on day {{day}}', { day: dia })
          : t('around day {{day}}', { day: dia }),
      );
    }
    const texto = partes.join(' · ');
    if (cobro.origen === 'manual') {
      return `${texto} ${t('(set by you)')}`;
    }
    return cobro.origen === 'historial'
      ? `${texto} ${t('(from your history)')}`
      : texto;
  };
}

function mayuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

const estiloFila = {
  minHeight: TACTIL + 4,
  borderRadius: 0,
  borderTop: `1px solid ${color.line}`,
  justifyContent: 'flex-start',
  padding: '6px 16px',
  backgroundColor: 'transparent',
  textAlign: 'left',
} as const;

/**
 * Detalle de un pago fijo en la ficha: importe y frecuencia (del objetivo),
 * día de cargo (guardado o del historial, editable) y cuenta de cargo (la
 * del historial se guarda sola al activar «Pago fijo»; sin historial se
 * pide elegirla; cambiable). Si el último cargo salió de otra cuenta,
 * pregunta si a partir de ahora sale de ahí.
 */
export function DetallePagoFijo({
  categoryId,
  month,
  objetivo,
  nombreCategoria = '',
}: {
  categoryId: string;
  month: string;
  objetivo: Objetivo | null | 'otro';
  nombreCategoria?: string;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const textoFrecuencia = useTextoFrecuencia();
  const { cuentas, elegidas, setCuenta } = useCuentasPagosFijos();
  const { data: todasCuentas = [] } = useAccounts();
  const ids = useMemo(() => [categoryId], [categoryId]);
  const historialCargado = useHistorialCategorias(ids, month);
  const historial = historialCargado?.get(categoryId) ?? undefined;
  const { guardar } = useGuardarObjetivo(categoryId, month);
  const { ignorados, ignorar } = useIgnoradosPagosFijos();
  const { frecuencias, setFrecuencia } = useFrecuenciasPagosFijos();
  const textoCobro = useTextoCobro();
  const textoCada = useTextoCadaMeses();
  const [hoja, setHoja] = useState<'dia' | 'cuenta' | 'frecuencia' | null>(
    null,
  );

  const guardada = cuentas[categoryId] ?? null;
  // Mismo dato que «Cuenta» de la ficha: «Todas las cuentas» también vale
  // para el pago fijo (sin cuenta de cargo fija ni pregunta de cambio).
  const todasLasCuentas = elegidas[categoryId] === TODAS_LAS_CUENTAS;
  const hayEleccion = categoryId in elegidas;
  const propuesta = inferirCuenta(historial ?? []);

  // «Todas salen siempre de la misma cuenta»: la del historial se guarda
  // sola, una vez (sin historial, se pide elegirla). Nunca pisa una
  // elección hecha (ni «Todas las cuentas»).
  const yaGuardada = useRef(false);
  useEffect(() => {
    if (historialCargado && !hayEleccion && propuesta && !yaGuardada.current) {
      yaGuardada.current = true;
      setCuenta(categoryId, propuesta);
    }
  }, [historialCargado, hayEleccion, propuesta, categoryId, setCuenta]);

  const valido =
    objetivo && objetivo !== 'otro' && frecuenciaDeObjetivo(objetivo)
      ? objetivo
      : null;
  if (!valido) {
    return (
      <Text
        style={{ padding: '10px 16px', fontSize: 14, color: color.fg2 }}
        data-testid="pago-fijo-sin-objetivo"
      >
        <Trans>
          Set a target for this category: the amount and how often it is charged
          come from it.
        </Trans>
      </Text>
    );
  }

  const diaInferido = inferirDiaCargo(historial ?? []);
  const dia = valido.tipo === 'mensual' ? (valido.dia ?? diaInferido) : null;
  const manual = frecuencias[categoryId] ?? null;
  const cobro = cobroReal(historial ?? [], manual, valido);
  const cuentaId = guardada ?? propuesta;
  const nombreCuenta = todasLasCuentas
    ? t('All accounts')
    : todasCuentas.find(c => c.id === cuentaId)?.name;
  const cambio = detectarCambioCuenta({
    historial: historial ?? [],
    guardada,
    mes: monthUtils.currentMonth(),
    ignorados,
  });
  const nombreNueva = cambio
    ? (todasCuentas.find(c => c.id === cambio.cuentaId)?.name ?? '')
    : '';

  const guardarDia = async (n: number) => {
    if (valido.tipo !== 'mensual') {
      return;
    }
    setHoja(null);
    await guardar(
      lineasDeObjetivo({ ...valido, dia: n }, monthUtils.currentDay()),
      t('Charge day saved.'),
    );
  };

  return (
    <View data-testid="pago-fijo-detalle">
      <Text
        style={{ padding: '10px 16px', fontSize: 14, color: color.fg2 }}
        data-testid="pago-fijo-importe"
      >
        {valido.tipo === 'mensual'
          ? t('You set aside {{amount}} a month', {
              amount: format(valido.importe, 'financial'),
            })
          : `${format(valido.importe, 'financial')} · ${textoFrecuencia(valido)}`}
      </Text>

      {valido.tipo === 'mensual' && (
        // Una sola línea: frecuencia, importe y día. Abre la hoja «¿Cada
        // cuánto se cobra?», que también deja cambiar el día de cargo.
        <Button
          variant="bare"
          onPress={() => setHoja('frecuencia')}
          style={{ ...estiloFila, fontSize: 14, color: color.fg }}
          data-testid="pago-fijo-cobro"
        >
          {textoCobro(cobro, dia, valido.dia != null)}
        </Button>
      )}

      {valido.tipo === 'mensual' ? (
        // Sin día (ni guardado ni en el historial): se pide, sin repetir
        // la frecuencia ni el importe de la línea de arriba.
        dia == null && cobro.meses !== 'irregular' ? (
          <Button
            variant="bare"
            onPress={() => setHoja('dia')}
            style={{ ...estiloFila, fontSize: 14, color: color.fg }}
            data-testid="pago-fijo-elegir-dia"
          >
            {t('Which day is it charged? Choose the day')}
          </Button>
        ) : null
      ) : 'fecha' in valido ? (
        <Text
          style={{
            ...estiloFila,
            display: 'flex',
            alignItems: 'center',
            fontSize: 14,
            color: color.fg,
          }}
          data-testid="pago-fijo-fecha"
        >
          {t('Charged on {{date}}', {
            date: monthUtils.format(valido.fecha, 'd MMMM yyyy', locale),
          })}
        </Text>
      ) : null}

      <Button
        variant="bare"
        onPress={() => setHoja('cuenta')}
        style={{
          ...estiloFila,
          fontSize: 14,
          color: nombreCuenta ? color.fg : color.warn,
        }}
        data-testid="pago-fijo-cuenta"
      >
        {nombreCuenta
          ? t('From: {{account}}', { account: nombreCuenta })
          : t('From which account? Choose the account')}
      </Button>

      {cambio && nombreNueva && (
        <View
          style={{
            ...estiloFila,
            gap: 8,
            alignItems: 'stretch',
          }}
          data-testid="pago-fijo-cambio-cuenta"
        >
          <Text style={{ fontSize: 14, color: color.warn }}>
            {t(
              '{{category}}: the last charge came from {{account}}. Charge it from there from now on?',
              { category: nombreCategoria, account: nombreNueva },
            )}
          </Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Boton
              zona={44}
              variante="primario"
              onPress={() => setCuenta(categoryId, cambio.cuentaId)}
              data-testid="pago-fijo-cambio-si"
            >
              <Trans>Yes, change</Trans>
            </Boton>
            <Boton
              zona={44}
              variante="fantasma"
              onPress={() => ignorar(cambio.movimientoId)}
              data-testid="pago-fijo-cambio-no"
            >
              <Trans>No</Trans>
            </Boton>
          </View>
        </View>
      )}

      {hoja === 'dia' && (
        <Hoja
          aria-label={t('Charge day')}
          onCerrar={() => setHoja(null)}
          data-testid="hoja-dia-cargo"
        >
          <Text
            style={{ fontWeight: 700, fontSize: 17, padding: '0 4px 12px' }}
          >
            <Trans>Which day of the month is it charged?</Trans>
          </Text>
          <View
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 6,
            }}
          >
            {Array.from({ length: 31 }, (_, i) => i + 1).map(n => (
              <Button
                key={n}
                variant={n === dia ? 'primary' : 'bare'}
                onPress={() => void guardarDia(n)}
                style={{ minHeight: TACTIL, fontSize: 16 }}
                aria-label={t('Day {{day}}', { day: n })}
              >
                {n}
              </Button>
            ))}
          </View>
        </Hoja>
      )}

      {hoja === 'frecuencia' && (
        <Hoja
          aria-label={t('How often is it charged?')}
          onCerrar={() => setHoja(null)}
          data-testid="hoja-frecuencia-cargo"
        >
          <Text
            style={{ fontWeight: 700, fontSize: 17, padding: '0 4px 12px' }}
          >
            <Trans>How often is it charged?</Trans>
          </Text>
          <View style={{ gap: 6 }}>
            {MESES_COBRO.map(n => (
              <Button
                key={n}
                variant={n === manual ? 'primary' : 'bare'}
                onPress={() => {
                  setFrecuencia(categoryId, n);
                  setHoja(null);
                }}
                style={{ minHeight: TACTIL, fontSize: 16 }}
                data-testid={`frecuencia-${n}`}
              >
                {mayuscula(textoCada(n))}
              </Button>
            ))}
            <Button
              variant={manual == null ? 'primary' : 'bare'}
              onPress={() => {
                setFrecuencia(categoryId, null);
                setHoja(null);
              }}
              style={{ minHeight: TACTIL, fontSize: 16 }}
              data-testid="frecuencia-historial"
            >
              <Trans>As in my history</Trans>
            </Button>
            {valido.tipo === 'mensual' && (
              <Button
                variant="bare"
                onPress={() => setHoja('dia')}
                style={{
                  minHeight: TACTIL,
                  fontSize: 16,
                  borderTop: `1px solid ${color.line}`,
                  marginTop: 6,
                }}
                data-testid="pago-fijo-dia"
              >
                {dia == null
                  ? t('Which day is it charged? Choose the day')
                  : t('Change the charge day (now day {{day}})', {
                      day: dia,
                    })}
              </Button>
            )}
          </View>
        </Hoja>
      )}

      {hoja === 'cuenta' && (
        // Mismas opciones que «Cuenta» de la ficha (el mismo dato).
        <HojaCuentaCategoria
          categoryId={categoryId}
          month={month}
          titulo={t('Charged from')}
          onCerrar={() => setHoja(null)}
          data-testid="hoja-cuenta-cargo"
        />
      )}
    </View>
  );
}
